import { useState } from 'react';
import { Plus, Trash2, CalendarDays, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTerms, useCreateTerm, useRemoveTerm, type Term } from '@/hooks/use-terms';
import type { Id } from '../../../convex/_generated/dataModel';
import { formatDate } from '@/lib/format';

export function TermsSettings() {
  const { data: terms, isLoading } = useTerms();
  const createTerm = useCreateTerm();
  const removeTerm = useRemoveTerm();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<Id<'terms'> | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) { setFormError('Term name is required.'); return; }
    if (!startDate || !endDate) { setFormError('Both dates are required.'); return; }
    if (startDate > endDate) { setFormError('Start date must be before end date.'); return; }

    setFormError(null);
    try {
      await createTerm.mutateAsync({ name: name.trim(), startDate, endDate });
      setName('');
      setStartDate('');
      setEndDate('');
      setShowForm(false);
    } catch {
      setFormError('Failed to create term. Please try again.');
    }
  };

  const handleDelete = async (termId: Id<'terms'>) => {
    try {
      await removeTerm.mutateAsync(termId);
      setConfirmDeleteId(null);
    } catch {
      // silently fail — term may already be gone
    }
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading terms...</p>;
  }

  return (
    <div className="space-y-3">
      {/* Term list */}
      {(!terms || terms.length === 0) ? (
        <p className="text-sm text-muted-foreground">
          No terms created yet. Add a term to start enrolling players and generating invoices.
        </p>
      ) : (
        <div className="space-y-2">
          {terms.map((term: Term) => (
            <div
              key={term.id}
              className="flex items-center justify-between p-3 rounded-lg border bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-800">{term.name}</div>
                  <div className="text-xs text-gray-500">
                    {formatDate(term.startDate)} – {formatDate(term.endDate)}
                  </div>
                </div>
              </div>

              {confirmDeleteId === term.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Deletes all enrollments
                  </span>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(term.id)}
                    disabled={removeTerm.isPending}
                    className="h-7 text-xs px-2"
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmDeleteId(null)}
                    className="h-7 text-xs px-2"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmDeleteId(term.id)}
                  className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add term form */}
      {showForm ? (
        <div className="border rounded-lg p-4 space-y-3 bg-white">
          <p className="text-sm font-medium text-gray-700">New Term</p>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Term name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Term 2 2026"
              className="h-8 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Start date *</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">End date *</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          </div>
          {formError && (
            <p className="text-xs text-red-500">{formError}</p>
          )}
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowForm(false); setFormError(null); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={createTerm.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {createTerm.isPending ? 'Saving…' : 'Save Term'}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowForm(true)}
          className="gap-1.5"
        >
          <Plus className="w-4 h-4" />
          Add Term
        </Button>
      )}
    </div>
  );
}
